import { Stripe, StripeConstructorOptions, loadStripe } from '@stripe/stripe-js';
import type { ValidationErrorResponseData } from '../services/exports/Types';
import type { CompanyDetailsInterface } from '../services/exports/Interfaces';

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]'];

/**
 * Stripe.js refuses a live key on a page served over plain HTTP and throws an unhandled
 * IntegrationError. A restaurant's custom domain answers on HTTP until its certificate
 * is issued, so on such a page Stripe is not loaded at all and the card and wallet
 * options degrade to absent instead of crashing checkout. Test keys only warn, and
 * Stripe allows localhost, so local development is unaffected.
 */
export function canLoadStripe(
  publishableKey: string,
  location: Pick<Location, 'protocol' | 'hostname'> = window.location,
): boolean {
  return (
    location.protocol === 'https:' ||
    LOCAL_HOSTNAMES.includes(location.hostname) ||
    !publishableKey.startsWith('pk_live_')
  );
}

export function loadStripeIfSecure(
  publishableKey: string,
  options?: StripeConstructorOptions,
): Promise<Stripe | null> | null {
  return canLoadStripe(publishableKey) ? loadStripe(publishableKey, options) : null;
}

type StripeChargeCompany = Pick<CompanyDetailsInterface, 'stripe_id' | 'stripe_charge_model'> | null | undefined;

/** Whether the restaurant's Stripe charges are destination charges on the partner's platform. */
export function chargesStripeDestination(company: StripeChargeCompany): boolean {
  return company?.stripe_charge_model === 'destination';
}

/**
 * Where Stripe.js runs for the restaurant's own charges, and the `on_behalf_of` the deferred Elements
 * are told. A destination charge lives on the platform, so Stripe.js runs account-less there and the
 * Elements are told the restaurant, which decides the methods, wallets and statement descriptor
 * Stripe offers. A direct charge (or a backend without the field) runs as the restaurant's account.
 */
export function resolveStripeInit(company: StripeChargeCompany): {
  stripeAccount: null | string;
  onBehalfOf: null | string;
} {
  if (chargesStripeDestination(company)) {
    return { stripeAccount: null, onBehalfOf: company?.stripe_id || null };
  }

  return { stripeAccount: company?.stripe_id ?? null, onBehalfOf: null };
}

/**
 * Stripe's origins the payment screen's Elements reach first: the script, the API the Elements open their
 * session on, and the fraud-signals frame. `crossorigin` where the request is a CORS fetch, so the warm
 * connection is the one the browser reuses.
 */
const STRIPE_ORIGINS: { href: string; crossOrigin: boolean }[] = [
  { href: 'https://js.stripe.com', crossOrigin: false },
  { href: 'https://api.stripe.com', crossOrigin: true },
  { href: 'https://m.stripe.network', crossOrigin: false },
];

/**
 * Opens the connections to Stripe ahead of the payment screen, from the checkout step, so the Elements
 * do not start with a cold DNS + TLS round trip each. Added there rather than in index.html: a
 * preconnect is a connection, and every visitor of every page would hand Stripe their IP before they
 * had chosen to pay (the reason DM Sans is self-hosted, see index.html). Once per page.
 */
export function preconnectStripe(doc: Document = document): void {
  STRIPE_ORIGINS.forEach(({ href, crossOrigin }) => {
    if (doc.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
      return;
    }

    const link = doc.createElement('link');

    link.rel = 'preconnect';
    link.href = href;

    if (crossOrigin) {
      link.crossOrigin = 'anonymous';
    }

    doc.head.appendChild(link);
  });
}

/**
 * An amount in the currency's minor units, as Stripe takes it: 12.5 EUR is 1250, 1250 JPY is 1250,
 * 1.5 KWD is 1500. The exponent is ISO 4217's, read from Intl; an unknown code falls back to two.
 */
export function toStripeMinorUnits(amount: number, currency: string): number {
  let exponent = 2;

  try {
    exponent =
      new Intl.NumberFormat('en', { style: 'currency', currency: currency.toUpperCase() }).resolvedOptions()
        .maximumFractionDigits ?? 2;
  } catch {
    exponent = 2;
  }

  return Math.round(amount * 10 ** exponent);
}

/** The fields the backend turns a Stripe Elements checkout away on (StripePaymentElementOffered). */
export const STRIPE_ELEMENT_RULES = ['stripe_confirmation_token', 'stripe_payment_method_type', 'stripe_wallet'];

/** The backend's own words for such a refusal: the checkout switched off, the method hidden, the details expired. */
export function stripeElementValidationMessage(response: ValidationErrorResponseData): string | undefined {
  return STRIPE_ELEMENT_RULES.map((field) => response.errors?.[field]?.[0]).find(Boolean);
}
